import styled from 'styled-components';
import { darken, rgba } from 'polished';

import _Modal from './Modal';
import Card from '../../components/Card';
import _Calendar from '../../components/Calendar';

interface CardItemProps {
  color?: string;
}

export const Container = styled.div`
  font-family: 'Sora', sans-serif;
  padding: 50px;
  display: flex;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.white};

  * {
    letter-spacing: 0px;
    line-height: initial;
  }

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoints.xxl}px) {
    padding: 30px;
  }

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    padding: 20px;
  }
`;

export const Wrapper = styled.div`
  max-width: 1500px;
  width: 100%;
  position: relative;

  header.wrapper-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    h1 {
      font-weight: 700;
      color: ${({ theme }) => theme.colors.black};
      font-size: 28px;
      letter-spacing: 0px;
    }

    h3 {
      color: ${({ theme }) => theme.colors.grey};
      font-weight: 400;
      padding-top: 5px;

      strong {
        color: ${({ theme }) => theme.colors.softBlack};
      }
    }

    div.row {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    button {
      display: flex;
      align-items: center;
      border: 3px solid ${({ theme }) => theme.colors.lightGrey};
      border-radius: 5px;
      background-color: ${({ theme }) => theme.colors.white};
      transition: background-color 0.2s ease;

      &:hover {
        background-color: ${({ theme }) => darken(0.03, theme.colors.white)};
      }
    }

    button.filter {
      justify-content: center;
      height: 51px;
      width: 51px;

      img {
        width: 24px;
      }

      &.active {
        border-color: ${({ theme }) => theme.colors.main};
      }
    }

    button.select-period,
    button.export-xlsx {
      margin-left: 5px;
      padding: 13px 15px;
      color: ${({ theme }) => theme.colors.softBlack};

      svg {
        font-size: 20px;
        margin-right: 8px;
        stroke-width: 2.5px;
      }

      span {
        font-family: 'Sora', sans-serif;
        font-size: 15px;
        font-weight: 600;
      }
    }
  }
`;

export const SmallCard = styled(Card)``;

export const Cards = styled.div`
  padding-top: 40px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(11, 128px);
  grid-column-gap: 30px;
  grid-row-gap: 30px;

  @media only screen and (max-width: ${({ theme }) =>
      theme.breakpoints.xxl}px) {
    padding-top: 30px;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(14, 133px);
    grid-column-gap: 20px;
    grid-row-gap: 20px;
  }

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.xl}px) {
    padding-top: 20px;
    grid-template-columns: 1fr;
    grid-template-rows: repeat(18, 138px);
    grid-column-gap: 10px;
    grid-row-gap: 10px;
  }
`;

export const CardItem = styled.div<CardItemProps>`
  div.wrapper {
    display: flex;
    align-items: center;

    div.icon-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      border-radius: 5px;
      background-color: ${({ color }) =>
        color ? rgba(color, 0.1) : 'transparent'};
      margin-right: 10px;
      overflow: hidden;

      svg {
        color: ${({ color }) => color || 'transparent'};
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    div.info {
      display: flex;
      flex-direction: column;
      flex: 1;

      header,
      footer {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: space-between;

        > strong {
          font-weight: 700;
          color: ${({ theme }) => theme.colors.softBlack};
          display: flex;
          align-items: flex-end;

          &:first-child {
            display: -webkit-box;
            overflow: hidden;
            text-overflow: ellipsis;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
          }

          &:last-child {
            white-space: nowrap;
            margin-left: 10px;
          }

          svg {
            margin-left: 5px;
          }
        }

        > span {
          font-size: 13px;
          color: ${({ theme }) => theme.colors.grey};
        }
      }

      footer {
        margin-top: 8px;
      }

      div.progress-bar {
        margin-top: 8px;
      }
    }
  }

  > div.progress-bar {
    width: calc(100% - 54px);
    margin: 8px 0 0 54px;
  }

  & + div {
    margin-top: 20px;
  }
`;

export const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 50px;
`;

export const Calendar = styled(_Calendar)`
  position: absolute;
  top: 55px;
  right: 0px;
`;

export const Modal = styled(_Modal)`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  padding: 15px 20px;
  width: 100%;
  max-width: 400px;
  max-height: 80%;
  overflow: auto;

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    h3 {
      font-weight: 700;
      color: ${({ theme }) => theme.colors.black};
    }

    button {
      padding: 5px;
      background: none;
      border: none;
      font-size: 26px;
      color: ${({ theme }) => theme.colors.red};
      max-width: 36px;
      max-height: 36px;
      transition: color 0.2s ease;

      &:hover {
        color: ${({ theme }) => darken(0.3, theme.colors.red)};
      }
    }
  }

  main {
    padding: 25px 0;
  }

  footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;

    button {
      display: flex;
      align-items: center;
      padding: 13px 18px;
      border-radius: 5px;
      background-color: ${({ theme }) => theme.colors.main};
      border: none;
      transition: background-color 0.2s ease;

      span {
        font-family: 'Sora', sans-serif;
        font-size: 15px;
        font-weight: 600;
        color: ${({ theme }) => theme.colors.white};
      }

      &:hover {
        background-color: ${({ theme }) => darken(0.03, theme.colors.main)};
      }
    }
  }
`;
